export interface Appointment {
  start_time: string
  end_time: string
  status: string
}

export interface AppointmentList {
  [id: string]: Appointment
}

export interface Balance {
  balance: string | number
}

export interface BalanceList {
  [id: string]: Balance
}

export interface Insurance {
  insurance_name: string
}

export interface InsuranceList {
  [id: string]: Insurance
}

export interface IntegrationData {
  insurance: InsuranceList
  balance: BalanceList
  appointment: AppointmentList
}

// Legacy contact type (v1) - version field is optional
export interface LineExtensionContactV1 {
  company_id: number
  contact_external_id: string
  contact_id: string
  contact_type: string
  guarantor_external_id: string
  raw_phone_number: string
  source: {
    name: string
    id: string
    manager: string
    version: string
  }
  name: {
    middle?: string
    last?: string
    prefix?: string
    suffix?: string
    locale?: string
    first?: string
    preferred?: string
    full?: string
  }
  is_preferred: boolean
  pbx_uuid: string
  pbx_id: number
  phone_type: string
  phone_number: string
  pk: string
  last_modified: string
  sk: string
  version?: number
}

// New contact type (v2) - version field is required and equals 2
export interface LineExtensionContactV2 {
  agent_id?: string
  company_id: string
  contact_external_id: string
  contact_id: string
  contact_type: string
  date_of_birth?: string
  guarantor_external_id: string
  is_preferred: boolean
  last_modified: string
  given_name?: string
  family_name?: string
  full_name?: string
  middle_name?: string
  preferred_name?: string
  prefix?: string
  suffix?: string
  name_as_source?: string
  pbx_id: number
  pbx_uuid: string
  phone_number: string
  phone_type: string
  phone_format?: string
  pk: string
  sk: string
  integration_data?: IntegrationData
  sources: {
    id: string
    source_manager: string
    source_name: string
  }[]
  external_identifiers: {
    id: string
    contact_fk_id: string
    source_fk_id: string
    contact_status: string
    contact_type: string
    external_id_value: string
    patient_display_id?: string
  }[]
  version: 2
  created_at?: string
  updated_at?: string
  processed_at?: string
  locale?: string
  gender?: string
  partner?: string
  // TODO: Integrations isn't providing this yet, so this will change.
  default_location?: string
}

// Union type for backward compatibility
export type LineExtensionContact =
  LineExtensionContactV1 | LineExtensionContactV2

export interface LineExtensionPayload {
  call_uuid: string
  caller_id_name: string
  caller_id_number: string
  extension_number: string
  extension_name: string
  cc_queue?: string
  cc_agent?: string
  mango_direction: 'inbound' | 'outbound'
  mango_destination?: string
  _mango_contacts: LineExtensionContact[]
  _mango_pbxUUID: string
  _mango_phoneNumber?: string
  _mango_eventName: string
  _mango_detailType: string
  _mango_sqsMessageId: string

  [key: string]: unknown
}

export interface PhoneNumberPreview {
  id: number
  pbx_id: number
  name: string
  created_at: string
  updated_at: string
}

export interface PhoneNumber {
  id: number
  name: string
  number: string
  number_for_display: string
  is_primary_sms: boolean
  campaign_id?: string | null
}

export enum PhoneCallStatus {
  INITIAL = 'INITIAL',
  CONNECTING = 'CONNECTING',
  RINGING = 'RINGING',
  CONNECTED = 'CONNECTED',
  ENDING_CALL = 'ENDING_CALL',
  ENDED = 'ENDED',
}

/**
 * Returns true for call statuses that represent a call that has not yet been
 * answered (INITIAL, CONNECTING, RINGING). Useful for filtering, DND, and
 * UI styling where all pre-connected states should be treated uniformly.
 */
export function isPreConnectedStatus(status: PhoneCallStatus): boolean {
  return (
    status === PhoneCallStatus.INITIAL ||
    status === PhoneCallStatus.CONNECTING ||
    status === PhoneCallStatus.RINGING
  )
}

export enum PhoneCallWsStatus {
  RINGING = 'RINGING',
  ANSWERED = 'ANSWERED',
  HANGUP = 'HANGUP',
  CONNECTED = 'CONNECTED',
  HOLD = 'HOLD',
  RESUME = 'RESUME',
}

export type PhoneCall = {
  status: PhoneCallStatus
  callUuid: string
  number: string
  name: string | null
  sipCallerId?: { number: string; name: string | null }
  destinationNumber?: string
  startTime: Date | null
  isOnHold: boolean
  isMuted: boolean
  callSource?: 'softphone' | 'deskphone'
  direction?: 'inbound' | 'outbound'
  isUnparked?: boolean
  isPageOrIntercom?: boolean
  contactInfo?: LineExtensionContact
  extensionNumber?: string
  extensionName?: string
  /** Mock call from the settings "Show example" popup preview; not a real call. */
  isExample?: boolean
}

export type PhoneCallWs = {
  lineExtensionState: PhoneCallWsStatus
  callUuid: string
  number: string
  name: string | null
  extensionNumber: string
  contactInfo: LineExtensionContact
}
